import { ArrayUtil, DynamicLoader, LayoutNameResolver, Navigation, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button } from "../button/Button";
import { ClickEvent } from "../../events/ClickEvent";
import { Component, Layout } from "../..";
import { ComponentUtil } from "../../base/ComponentUtil";
import { ContextMenuLabel } from "./ContextMenuLabel";
import { ContextMenuItemsProvider, ContextMenuLabelProps } from "./ContextMenuLabelProps";
import { ContextMenuProps } from "./ContextMenu";
import { DataSourceMode } from "../../databinding/DataSource";
import { Label } from "../label/Label";
import { ListItem } from "../list/ListItem";
import { ListLayoutLauncher } from "../../page/ListLayoutLauncher";
import { Textbox } from "../textbox/Textbox";

export class ContextMenuItemCreator {
    static createListItems(props: ContextMenuProps): ListItem[] {
        const { sourceComponent, designer, itemProvider = props.sourceComponent.contextMenuItems } = props;

        const labelProps: Partial<ContextMenuLabelProps>[] = [];

        if (props?.designer == null) {
            labelProps.push(...this.getStandardContextItems(sourceComponent));
        }

        // Turn the provided input (possibly a function) into ContextMenuLabelProps, and add them to the array
        labelProps.push(...ContextMenuItemCreator.resolveContextMenuItemProps(itemProvider));

        // Let the component add to or adjust the array of ContextMenuLabels if it wants to
        props.sourceComponent.finalizeContextMenuLabelProps(labelProps);

        // Create a ContextMenuLabel for each labelProps object supplied
        const labels = labelProps.map(props => new ContextMenuLabel(sourceComponent, {_designer: designer, ...props }));

        // Make sure the last ContextMenuLabel doesn't try to render the separator after itself
        const lastLabel: ContextMenuLabel = ArrayUtil.getLastElement(labels);
        if (lastLabel != null) {
            lastLabel.separatorAfter = false;
        }

        // Return an array of ListItems (each wraps a ContextMenuLabel)
        return labels.map(label => new ListItem(label));
    }

    private static resolveContextMenuItemProps(provider: ContextMenuItemsProvider): Partial<ContextMenuLabelProps>[] {
        const labelProps = typeof provider === "function" ? provider() : provider;
        return (labelProps ?? [])
            .map(props => typeof props === "function" ? props() : props)
            .filter(props => props != null);
    }

    private static getStandardContextItems(sourceComponent: Component): Partial<ContextMenuLabelProps>[] {
        const result = [];
        if (sourceComponent instanceof Textbox || sourceComponent instanceof Label) {
            this.addListLayoutProps(sourceComponent, result);
        }
        this.addAuditLogProps(sourceComponent, result);
        return result;
    }

    private static addListLayoutProps(component: Textbox | Label, props: Partial<ContextMenuLabelProps>[]) {
        if (component == null) return;
        const launcherProps = component.listLayoutLauncherProps;
        if (launcherProps == null) return;
        const listLayout = LayoutNameResolver.resolve(launcherProps.listLayout);
        if (listLayout != null && component?.owner?.layoutName != listLayout) {
            props.push({
                text: this.resolveListLayoutCaption(component),
                imageName: ImageName.arrowGoTo,
                onClick: () => ListLayoutLauncher.open({...launcherProps})
            });
        }
    }

    // determines a caption for the ListLayout launcher context menu item
    private static resolveListLayoutCaption(component: Textbox | Label) {
        // first check if this component's listLayout is defined in the main menu
        const menuItem = DynamicLoader.getModuleByName("common/menu/MainMenu")?.MainMenu?.findMenuItemForPath?.(component.listLayout);
        if (menuItem != null) {
            // return the appropriate caption from the menu item
            const caption = menuItem?.favoriteCaption ?? menuItem?.caption;
            if (!StringUtil.isEmptyString(caption))
                return caption;
        }
        // determine a caption based on the component properties
        let resolvedCaption = ComponentUtil.resolveCaption(component);
        if (resolvedCaption != null) {
            resolvedCaption = StringUtil.removeTrailingString(resolvedCaption, "(s)");
        }
        return StringUtil.addSuffix(resolvedCaption, "s") ?? "Listing";
    }

    private static addAuditLogProps(component: Component, props: Partial<ContextMenuLabelProps>[]) {
        if (component == null) return;
        const hasValidField = component.field != null && !component.field.includes(".");
        const inValidMode = component.dataSource?.mode === DataSourceMode.UPDATE ||
            component.dataSource?.mode === DataSourceMode.NONE;
        const parentLayout = component.owner instanceof Layout ? component.owner : component.getParentLayout();

        if (hasValidField && inValidMode && !StringUtil.isEmptyString(parentLayout?.layoutName)) {
            props.push({
                imageName: ImageName.list,
                text: "Audit log",
                onClick: () => component.showAuditLog()
            });
        }
    }

    /**
     * Creates an array of ContextMenuLabelProps from a list of button configurations.
     * Each configuration includes a required `button` and optional properties to override the defaults.
     *
     * @param buttonConfigs - A list of configurations where each item contains:
     *   - `button`: The source button to derive the default properties.
     *   - Other optional properties to override the defaults (e.g., `text`, `visible`).
     *
     * @returns An array of context menu label properties based on the provided buttons and overrides.
     */
    static fromButtons(
        ...buttonConfigs: ({ button: Button } & Partial<ContextMenuLabelProps>)[]
    ): Partial<ContextMenuLabelProps>[] {
        return buttonConfigs.map(({ button, ...props }: { button: Button } & Partial<ContextMenuLabelProps>) =>
            ContextMenuItemCreator.fromButton(button, props)
        );
    }

    /**
    * Creates a single ContextMenuLabelProps object from a button.
    * Default properties are derived from the button, and any provided overrides are applied.
    *
    * @param button - The source button to derive the default properties.
    * @param overrideProps - Optional properties to override the defaults (e.g., `text`, `visible`).
    *
    * @returns A context menu label property object with the merged properties.
    */
    static fromButton(button: Button, overrideProps?: Partial<ContextMenuLabelProps>): Partial<ContextMenuLabelProps> {
        return {
            text: button.caption ?? button.displayLabel,
            onClick: (event: ClickEvent) => button.clicked(event),
            visible: button.parent != null && button.visible && button.enabled,
            imageName: button.imageName,
            imageRotation: button.imageRotation,
            imageProps: button.imageProps,
            ...overrideProps,
        };
    }

    /**
     * Creates context menu items for Opening a layout in a new tab or window.
     *
     * @param layoutName - The name of the layout used to construct the navigation path.
     * @param key - The key to include in the navigation path query string.
     *
     * @returns An array of ContextMenuLabelProps or empty array if the key is not provided.
     */
    public static createNavigationContextMenuItems(layoutName: string, key: string): Partial<ContextMenuLabelProps>[] {
        if (!key) {
            return [];
        }
        const path = `${layoutName}?mode=update&key=${key}`;
        return [
            {
                text: "Open in New Tab",
                id: "openInNewTab",
                onClick: () => Navigation.navigateTo(path, { newTab: true })
            },
            {
                text: "Open in New Window",
                id: "openInNewWindow",
                onClick: () => Navigation.navigateTo(path, { newTab: true, windowDecorators: false })
            }
        ];
    }
}
